import { useState, useEffect, useCallback, useMemo } from 'react'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Thermometer,
  MessageSquare,
  History,
  Palette,
  Scale,
  Target,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

interface LLMConfig {
  temperature: number
  maxTokens: number
  historyLimit: number
}

interface LLMConfigSectionProps {
  config: LLMConfig
  onConfigChange: (config: LLMConfig) => void
  disabled?: boolean
}

enum ConfigType {
  PRECISE = 'precise',
  BALANCED = 'balanced',
  CREATIVE = 'creative',
}

export default function LLMConfigSection({
  config,
  onConfigChange,
  disabled = false,
}: LLMConfigSectionProps) {
  const [temperature, setTemperature] = useState([config.temperature])
  const [maxTokens, setMaxTokens] = useState(config.maxTokens.toString())
  const [historyLimit, setHistoryLimit] = useState(
    config.historyLimit.toString()
  )
  const [selectedPreset, setSelectedPreset] = useState<ConfigType | null>(null)

  const getPresetFromConfig = useCallback(
    (temp: number, tokens: number, history: number): ConfigType | null => {
      if (temp === 0.1 && tokens === 1024 && history === 5)
        return ConfigType.PRECISE
      if (temp === 0.7 && tokens === 2048 && history === 10)
        return ConfigType.BALANCED
      if (temp === 0.9 && tokens === 3072 && history === 20)
        return ConfigType.CREATIVE

      if (temp <= 0.3) return ConfigType.PRECISE
      if (temp <= 0.7) return ConfigType.BALANCED
      return ConfigType.CREATIVE
    },
    []
  )

  useEffect(() => {
    setTemperature([config.temperature])
    setMaxTokens(config.maxTokens.toString())
    setHistoryLimit(config.historyLimit.toString())

    const detectedPreset = getPresetFromConfig(
      config.temperature,
      config.maxTokens,
      config.historyLimit
    )
    setSelectedPreset(detectedPreset)
  }, [config, getPresetFromConfig])

  const handleTemperatureChange = useCallback(
    (value: number[]) => {
      setTemperature(value)

      const newPreset = getPresetFromConfig(
        value[0],
        parseInt(maxTokens) || 0,
        parseInt(historyLimit) || 0
      )
      setSelectedPreset(newPreset)

      onConfigChange({
        temperature: value[0],
        maxTokens: parseInt(maxTokens) || 0,
        historyLimit: parseInt(historyLimit) || 0,
      })
    },
    [maxTokens, historyLimit, onConfigChange, getPresetFromConfig]
  )

  const handleMaxTokensChange = useCallback(
    (value: string) => {
      setMaxTokens(value)
      const numValue = parseInt(value)
      if (numValue >= 1 && numValue <= 8192) {
        const newPreset = getPresetFromConfig(
          temperature[0],
          numValue,
          parseInt(historyLimit) || 0
        )
        setSelectedPreset(newPreset)

        onConfigChange({
          temperature: temperature[0],
          maxTokens: numValue,
          historyLimit: parseInt(historyLimit) || 0,
        })
      } else if (value === '') {
        onConfigChange({
          temperature: temperature[0],
          maxTokens: 0,
          historyLimit: parseInt(historyLimit) || 0,
        })
      }
    },
    [temperature, historyLimit, onConfigChange, getPresetFromConfig]
  )

  const handleHistoryLimitChange = useCallback(
    (value: string) => {
      setHistoryLimit(value)
      const numValue = parseInt(value)
      if (numValue >= 1 && numValue <= 50) {
        const newPreset = getPresetFromConfig(
          temperature[0],
          parseInt(maxTokens) || 0,
          numValue
        )
        setSelectedPreset(newPreset)

        onConfigChange({
          temperature: temperature[0],
          maxTokens: parseInt(maxTokens) || 0,
          historyLimit: numValue,
        })
      } else if (value === '') {
        onConfigChange({
          temperature: temperature[0],
          maxTokens: parseInt(maxTokens) || 0,
          historyLimit: 0,
        })
      }
    },
    [temperature, maxTokens, onConfigChange, getPresetFromConfig]
  )

  const handlePreset = useCallback(
    (preset: ConfigType) => {
      let newConfig: LLMConfig

      setSelectedPreset(preset)

      switch (preset) {
        case ConfigType.PRECISE:
          newConfig = { temperature: 0.1, maxTokens: 1024, historyLimit: 5 }
          break
        case ConfigType.BALANCED:
          newConfig = { temperature: 0.7, maxTokens: 2048, historyLimit: 10 }
          break
        case ConfigType.CREATIVE:
          newConfig = { temperature: 0.9, maxTokens: 3072, historyLimit: 20 }
          break
        default:
          return
      }

      setTemperature([newConfig.temperature])
      setMaxTokens(newConfig.maxTokens.toString())
      setHistoryLimit(newConfig.historyLimit.toString())
      onConfigChange(newConfig)
    },
    [onConfigChange]
  )

  const getTemperatureLabel = useMemo(
    () => (temp: number) => {
      if (temp <= 0.3)
        return { label: 'Precise', color: 'bg-blue-100 text-blue-800' }
      if (temp <= 0.7)
        return { label: 'Balanced', color: 'bg-green-100 text-green-800' }
      return { label: 'Creative', color: 'bg-purple-100 text-purple-800' }
    },
    []
  )

  const tempInfo = getTemperatureLabel(temperature[0])

  return (
    <TooltipProvider>
      <div className='space-y-6'>
        <div className='flex space-x-3'>
          <h2 className='text-2xl font-semibold text-foreground'>
            Model Configuration
          </h2>
          <Badge variant='secondary' className='text-xs'>
            Advanced
          </Badge>
        </div>
        <Card>
          <CardHeader className='pb-4'>
            <p className='text-sm text-muted-foreground'>
              Fine-tune the AI model's behavior and response characteristics
            </p>
          </CardHeader>

          <CardContent className='space-y-6'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Thermometer className='w-4 h-4 ' />
                  <Label htmlFor='temperature' className='text-sm font-medium'>
                    Temperature
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <QuestionMarkCircleIcon className='w-4 h-4 text-muted-foreground cursor-help' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-xs'>
                      <p>
                        Controls randomness: 0.0 = focused and deterministic,
                        1.0 = creative and varied
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className='flex items-center gap-2'>
                  <Badge className={tempInfo.color} variant='secondary'>
                    {tempInfo.label}
                  </Badge>
                  <span className='text-sm font-mono bg-muted px-2 py-1 rounded'>
                    {temperature[0]}
                  </span>
                </div>
              </div>

              <div className='px-2'>
                <Slider
                  id='temperature'
                  min={0.1}
                  max={1}
                  step={0.1}
                  value={temperature}
                  onValueChange={handleTemperatureChange}
                  className='w-full'
                  disabled={disabled}
                />
                <div className='flex justify-between text-xs text-muted-foreground mt-1'>
                  <span>Focused</span>
                  <span>Balanced</span>
                  <span>Creative</span>
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <MessageSquare className='w-4 h-4 text-green-600' />
                <Label htmlFor='maxTokens' className='text-sm font-medium'>
                  Max Tokens
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <QuestionMarkCircleIcon className='w-4 h-4 text-muted-foreground cursor-help' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      Maximum length of AI responses. Higher values allow longer
                      responses but use more resources.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className='flex items-center space-x-2'>
                <Input
                  id='maxTokens'
                  type='number'
                  min='1'
                  max='4096'
                  value={maxTokens}
                  onChange={(e) => handleMaxTokensChange(e.target.value)}
                  className={cn(
                    'w-24',
                    maxTokens === '' && 'border-red-500 focus:border-red-500'
                  )}
                  disabled={disabled}
                />
                <div className='flex-1'>
                  <div className='text-xs text-muted-foreground'>
                    Recommended: 1024-4096 tokens
                  </div>
                  <div className='w-full bg-muted rounded-full h-1.5 mt-1'>
                    <Progress
                      value={Math.min((parseInt(maxTokens) / 4096) * 100, 100)}
                      className={cn(
                        'h-1.5',
                        parseInt(maxTokens) <= 2048
                          ? '[&>div]:bg-green-500'
                          : parseInt(maxTokens) <= 4096
                            ? '[&>div]:bg-yellow-500'
                            : '[&>div]:bg-orange-500'
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <History className='w-4 h-4 text-orange-600' />
                <Label htmlFor='historyLimit' className='text-sm font-medium'>
                  Conversation History Limit
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <QuestionMarkCircleIcon className='w-4 h-4 text-muted-foreground cursor-help' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs'>
                    <p>
                      Number of previous messages the AI remembers. Higher
                      values provide better context but use more memory.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className='flex items-center space-x-4'>
                <div className='flex items-center space-x-2'>
                  <Input
                    id='historyLimit'
                    type='number'
                    min='1'
                    max='50'
                    value={historyLimit}
                    onChange={(e) => handleHistoryLimitChange(e.target.value)}
                    className={cn(
                      'w-20',
                      historyLimit === '' &&
                        'border-red-500 focus:border-red-500'
                    )}
                    disabled={disabled}
                  />
                  <span className='text-sm text-muted-foreground'>
                    messages
                  </span>
                </div>

                <div className='flex-1 space-y-1'>
                  <div className='flex justify-between text-xs'>
                    <span className='text-muted-foreground'>Memory usage</span>
                    <span className='text-muted-foreground'>
                      {parseInt(historyLimit) <= 10
                        ? 'Low'
                        : parseInt(historyLimit) <= 25
                          ? 'Medium'
                          : 'High'}
                    </span>
                  </div>
                  <div className='w-full bg-muted rounded-full h-1.5'>
                    <Progress
                      value={Math.min((parseInt(historyLimit) / 50) * 100, 100)}
                      className={cn(
                        'h-1.5',
                        parseInt(historyLimit) <= 10
                          ? '[&>div]:bg-green-500'
                          : parseInt(historyLimit) <= 25
                            ? '[&>div]:bg-yellow-500'
                            : '[&>div]:bg-orange-500'
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <Label className='text-sm font-medium'>Quick Presets</Label>
              <div className='flex flex-wrap gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handlePreset(ConfigType.PRECISE)
                  }}
                  disabled={disabled}
                  className={cn(
                    'px-3 py-1.5 text-xs transition-colors',
                    selectedPreset === ConfigType.PRECISE
                      ? 'bg-dark-blue text-white border-dark-blue shadow-md'
                      : 'bg-sky-50 text-dark-blue border-sky-200 hover:bg-dark-blue-hovered'
                  )}
                >
                  <Target className='w-3 h-3 mr-1' />
                  Precise
                </Button>

                <Button
                  variant='outline'
                  size='sm'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handlePreset(ConfigType.BALANCED)
                  }}
                  disabled={disabled}
                  className={cn(
                    'px-3 py-1.5 text-xs transition-colors',
                    selectedPreset === ConfigType.BALANCED
                      ? 'bg-green-500 text-white border-green-500 shadow-md'
                      : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  )}
                >
                  <Scale className='w-3 h-3 mr-1' />
                  Balanced
                </Button>

                <Button
                  variant='outline'
                  size='sm'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handlePreset(ConfigType.CREATIVE)
                  }}
                  disabled={disabled}
                  className={cn(
                    'px-3 py-1.5 text-xs transition-colors',
                    selectedPreset === ConfigType.CREATIVE
                      ? 'bg-purple-500 text-white border-purple-500 shadow-md'
                      : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                  )}
                >
                  <Palette className='w-3 h-3 mr-1' />
                  Creative
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
}
